import { FlexRow } from '@/components/common/Flex';
import {
    CreditCardPlus,
    Edit05,
    Link04,
    PieChart03,
} from '@/components/common/icons';
import { Box, Tooltip } from '@chakra-ui/react';
import { BudgetOnboardingStep } from '@common/react/budget';
import { useBudgetOnboardingContext } from './BudgetOnboardingContext';

const TOOLTIPS: Array<{
    label: string;
    icon: JSX.Element;
    steps: BudgetOnboardingStep[];
}> = [
    { label: 'Link accounts', steps: ['link-accounts'], icon: <Link04 /> },
    { label: 'Your income', steps: ['income'], icon: <CreditCardPlus /> },
    {
        label: 'Your budget',
        steps: [
            'fixed-expenses',
            'lifestyle-expenses',
            'saving-investing',
            'transactions-syncing',
        ],
        icon: <PieChart03 />,
    },
    { label: 'Confirmation', steps: ['confirmation'], icon: <Edit05 /> },
];

export const BudgetOnboardingSteps = () => {
    const { currentStep, visitedSteps } = useBudgetOnboardingContext();

    const currentStepIndex = TOOLTIPS.findIndex(({ steps }) =>
        steps.includes(currentStep)
    );

    // Find the highest step index that has been visited
    const highestVisitedIndex = Math.max(
        ...TOOLTIPS.map((tooltip, index) =>
            tooltip.steps.some((step) => visitedSteps.has(step)) ? index : -1
        )
    );

    return (
        <FlexRow alignItems="center">
            {TOOLTIPS.map(({ label, icon, steps }, index) => (
                <StepTooltip
                    key={steps[0]}
                    step={steps[0]}
                    label={label}
                    isActive={currentStepIndex === index}
                    isDisabled={index > highestVisitedIndex}
                >
                    {icon}
                </StepTooltip>
            ))}
        </FlexRow>
    );
};

interface StepTooltipProps {
    label: string;
    children: React.ReactNode;
    isActive: boolean;
    isDisabled: boolean;
    step: BudgetOnboardingStep;
}

const StepTooltip = ({
    label,
    children,
    isActive,
    isDisabled,
    step,
}: StepTooltipProps) => {
    const { setCurrentStep } = useBudgetOnboardingContext();

    return (
        <Tooltip label={label} placement="top">
            <Box
                mx={3}
                opacity={isActive ? 1 : 0.2}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                onClick={() => !isDisabled && setCurrentStep(step)}
            >
                {children}
            </Box>
        </Tooltip>
    );
};
